import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { useAuth, useFirestore } from "reactfire";
import * as XLSX from "xlsx";
import { useFeedback } from "../../../providers/feedback/feedback";
import {
  computeMinimumDurationSeconds,
  computeRecommendedDurationSeconds,
  DEFAULT_QUIZ_SETTINGS,
  EMPTY_QUIZ_QUESTION,
  formatDurationLabel,
  formatQuizDateTime,
  slugifyQuestionId,
  YOUCAT_QUIZ_SETTINGS_COLLECTION,
  YOUCAT_QUIZ_SETTINGS_DOC_ID,
  YOUCAT_QUIZ_SUBMISSIONS_COLLECTION,
  YoucatQuizQuestion,
  YoucatQuizQuestionType,
  YoucatQuizSettings,
  YoucatQuizSubmission,
} from "../../youcat/quiz/quiz_shared";

function downloadQuizWorkbook(
  submissions: YoucatQuizSubmission[],
  quizTitle: string,
) {
  const workbook = XLSX.utils.book_new();
  const rows = submissions.map((submission) => {
    const row: Record<string, string | number> = {
      "Quiz Title": submission.quizTitle,
      "Participant Name": submission.userProfile?.fullName || "",
      Deanery: submission.userProfile?.deanery || "",
      Parish: submission.userProfile?.parish || "",
      Email: submission.userProfile?.email || "",
      Contact: submission.userProfile?.contact || "",
      Status: submission.status,
      Approval: submission.approvalStatus,
      "Started At": formatQuizDateTime(submission.startedAt),
      "Submitted At": formatQuizDateTime(submission.submittedAt),
      "Auto Score": submission.autoScore || 0,
      "Final Score":
        typeof submission.finalScore === "number"
          ? submission.finalScore
          : submission.autoScore || 0,
      "Maximum Score": submission.maxScore || 0,
    };

    submission.questionSnapshots.forEach((question, index) => {
      row[`Q${index + 1}: ${question.question}`] =
        submission.answers?.[question.id] || "";
    });

    return row;
  });

  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(rows),
    "Quiz Attempts",
  );
  XLSX.writeFile(
    workbook,
    `${(quizTitle || "YouCat Quiz").replace(/[^a-z0-9]+/gi, " ").trim()}.xlsx`,
  );
}

function parseDurationInput(value: string) {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return 0;
  }

  if (!trimmedValue.includes(":")) {
    return Math.max(0, Number(trimmedValue) || 0);
  }

  const [minutesPart, secondsPart] = trimmedValue.split(":");
  const minutes = Math.max(0, Number(minutesPart) || 0);
  const seconds = Math.max(0, Number(secondsPart) || 0);

  return minutes * 60 + seconds;
}

export default function YoucatQuizTab() {
  const firestore = useFirestore();
  const auth = useAuth();
  const { showSnackbar } = useFeedback();
  const [formValues, setFormValues] = useState<YoucatQuizSettings>(
    DEFAULT_QUIZ_SETTINGS,
  );
  const [submissions, setSubmissions] = useState<YoucatQuizSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [reviewingSubmissionId, setReviewingSubmissionId] = useState("");
  const [selectedSubmission, setSelectedSubmission] =
    useState<YoucatQuizSubmission | null>(null);
  const [durationInput, setDurationInput] = useState("0:00");
  const [reviewScoresByQuestion, setReviewScoresByQuestion] = useState<
    Record<string, string>
  >({});
  const [reviewRemarks, setReviewRemarks] = useState("");

  const recommendedDurationSeconds = useMemo(
    () => computeRecommendedDurationSeconds(formValues.questions),
    [formValues.questions],
  );
  const minimumDurationSeconds = useMemo(
    () => computeMinimumDurationSeconds(formValues.questions),
    [formValues.questions],
  );

  async function loadQuizData() {
    try {
      setIsLoading(true);
      const [settingsSnapshot, submissionsSnapshot] = await Promise.all([
        getDoc(
          doc(
            firestore,
            YOUCAT_QUIZ_SETTINGS_COLLECTION,
            YOUCAT_QUIZ_SETTINGS_DOC_ID,
          ),
        ),
        getDocs(
          query(
            collection(firestore, YOUCAT_QUIZ_SUBMISSIONS_COLLECTION),
            orderBy("startedAt", "desc"),
          ),
        ),
      ]);

      if (settingsSnapshot.exists()) {
        setFormValues((prev) => ({
          ...prev,
          ...(settingsSnapshot.data() as Partial<YoucatQuizSettings>),
        }));
      }

      setSubmissions(
        submissionsSnapshot.docs.map((submissionDoc) => ({
          docId: submissionDoc.id,
          ...(submissionDoc.data() as Omit<YoucatQuizSubmission, "docId">),
        })),
      );
    } catch (error) {
      console.error("Failed to load YouCat quiz settings", error);
      showSnackbar("Failed to load YouCat quiz data");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadQuizData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDurationInput(formatDurationLabel(formValues.totalDurationSeconds || 0));
  }, [formValues.totalDurationSeconds]);

  function handleAddQuestion() {
    setFormValues((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          ...EMPTY_QUIZ_QUESTION,
          id: `question-${prev.questions.length + 1}-${Date.now()}`,
        },
      ],
    }));
  }

  function handleQuestionChange(
    index: number,
    field: keyof YoucatQuizQuestion,
    value: string | number | boolean | string[],
  ) {
    setFormValues((prev) => ({
      ...prev,
      questions: prev.questions.map((question, questionIndex) =>
        questionIndex === index ? { ...question, [field]: value } : question,
      ),
    }));
  }

  async function handleDeleteQuiz() {
    if (!window.confirm("Delete the current quiz configuration? Existing submissions will be kept for records.")) {
      return;
    }

    try {
      setIsSaving(true);
      await deleteDoc(
        doc(
          firestore,
          YOUCAT_QUIZ_SETTINGS_COLLECTION,
          YOUCAT_QUIZ_SETTINGS_DOC_ID,
        ),
      );

      await setDoc(
        doc(firestore, "youcatPageSettings", "content"),
        {
          quizEnabled: false,
          quizTitle: "YouCat Quiz",
          quizDescription:
            "Login or signup to access the quiz section when the quiz is published.",
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      );

      setFormValues(DEFAULT_QUIZ_SETTINGS);
      setDurationInput(formatDurationLabel(DEFAULT_QUIZ_SETTINGS.totalDurationSeconds));
      showSnackbar("Quiz configuration deleted successfully");
    } catch (error) {
      console.error("Failed to delete quiz configuration", error);
      showSnackbar("Failed to delete quiz configuration");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSave() {
    const sanitizedQuestions = formValues.questions
      .filter((question) => question.question.trim())
      .map((question, index) => ({
        ...question,
        id:
          question.id ||
          `${index + 1}-${slugifyQuestionId(question.question.trim())}`,
        question: question.question.trim(),
        points: Math.max(0, Number(question.points) || 0),
        options:
          question.type === "mcq"
            ? question.options.map((option) => option.trim()).filter(Boolean)
            : [],
        correctOption:
          question.type === "mcq" ? question.correctOption.trim() : "",
        acceptedAnswers:
          question.type === "short_answer"
            ? question.acceptedAnswers.map((item) => item.trim()).filter(Boolean)
            : [],
        keywords: question.keywords.map((item) => item.trim()).filter(Boolean),
      }));

    if (!formValues.startAt || !formValues.endAt) {
      showSnackbar("Please set both start and end date and time");
      return;
    }

    if (new Date(formValues.endAt) <= new Date(formValues.startAt)) {
      showSnackbar("Quiz end date and time must be after the start date and time");
      return;
    }

    if (!sanitizedQuestions.length) {
      showSnackbar("Add at least one quiz question");
      return;
    }

    const nextRecommendedDuration =
      computeRecommendedDurationSeconds(sanitizedQuestions);
    const nextMinimumDuration =
      computeMinimumDurationSeconds(sanitizedQuestions);
    const totalDurationSeconds = Math.max(
      parseDurationInput(durationInput) || nextRecommendedDuration,
      nextMinimumDuration,
    );

    try {
      setIsSaving(true);
      const payload: YoucatQuizSettings = {
        ...formValues,
        quizId: formValues.quizId || "active-youcat-quiz",
        title: formValues.title.trim(),
        description: formValues.description.trim(),
        instructions: formValues.instructions.trim(),
        questions: sanitizedQuestions,
        totalDurationSeconds,
        recommendedDurationSeconds: nextRecommendedDuration,
        minimumDurationSeconds: nextMinimumDuration,
        updatedAt: new Date().toISOString(),
        isQuizAvailable: Boolean(formValues.isQuizAvailable),
      };

      await setDoc(
        doc(
          firestore,
          YOUCAT_QUIZ_SETTINGS_COLLECTION,
          YOUCAT_QUIZ_SETTINGS_DOC_ID,
        ),
        payload,
        { merge: true },
      );

      await setDoc(
        doc(firestore, "youcatPageSettings", "content"),
        {
          quizEnabled: payload.isQuizAvailable,
          quizTitle: payload.title,
          quizDescription: payload.description,
          updatedAt: payload.updatedAt,
        },
        { merge: true },
      );

      setFormValues(payload);
      showSnackbar("YouCat quiz settings saved successfully");
    } catch (error) {
      console.error("Failed to save YouCat quiz settings", error);
      showSnackbar("Failed to save YouCat quiz settings");
    } finally {
      setIsSaving(false);
    }
  }

  function openReviewDialog(submission: YoucatQuizSubmission) {
    setSelectedSubmission(submission);
    setReviewScoresByQuestion(
      submission.questionSnapshots.reduce<Record<string, string>>(
        (acc, question) => {
          const existingScore =
            submission.finalScoresByQuestion?.[question.id] ??
            submission.evaluationByQuestion?.[question.id]?.autoAwardedPoints ??
            0;
          acc[question.id] = String(existingScore);
          return acc;
        },
        {},
      ),
    );
    setReviewRemarks(submission.reviewRemarks || "");
  }

  async function handleApproveSubmission(
    approvalStatus: "approved" | "rejected" | "pending",
  ) {
    if (!selectedSubmission) {
      return;
    }

    const finalScoresByQuestion = selectedSubmission.questionSnapshots.reduce<
      Record<string, number>
    >((acc, question) => {
      const parsed = Number(reviewScoresByQuestion[question.id] || 0);
      acc[question.id] = Math.max(0, Math.min(question.points, parsed || 0));
      return acc;
    }, {});

    const finalScore = Number(
      Object.values(finalScoresByQuestion)
        .reduce((total, current) => total + current, 0)
        .toFixed(2),
    );

    try {
      setReviewingSubmissionId(selectedSubmission.docId);
      await updateDoc(
        doc(
          firestore,
          YOUCAT_QUIZ_SUBMISSIONS_COLLECTION,
          selectedSubmission.docId,
        ),
        {
          approvalStatus,
          finalScoresByQuestion,
          finalScore,
          reviewRemarks: reviewRemarks.trim(),
          reviewedAt: new Date().toISOString(),
          reviewedBy: auth.currentUser?.uid || "",
        },
      );

      setSubmissions((prev) =>
        prev.map((item) =>
          item.docId === selectedSubmission.docId
            ? {
                ...item,
                approvalStatus,
                finalScoresByQuestion,
                finalScore,
                reviewRemarks: reviewRemarks.trim(),
                reviewedAt: new Date().toISOString(),
                reviewedBy: auth.currentUser?.uid || "",
              }
            : item,
        ),
      );

      showSnackbar(
        approvalStatus === "approved"
          ? "Quiz attempt approved and final marks saved"
          : approvalStatus === "rejected"
            ? "Quiz attempt marked for follow-up"
            : "Quiz review saved",
      );
      setSelectedSubmission(null);
    } catch (error) {
      console.error("Failed to review quiz submission", error);
      showSnackbar("Failed to review quiz submission");
    } finally {
      setReviewingSubmissionId("");
    }
  }

  const pendingSubmissions = useMemo(
    () =>
      submissions.filter(
        (item) =>
          item.quizId === formValues.quizId && item.approvalStatus !== "approved",
      ),
    [formValues.quizId, submissions],
  );
  const approvedSubmissions = useMemo(
    () =>
      submissions.filter(
        (item) =>
          item.quizId === formValues.quizId && item.approvalStatus === "approved",
      ),
    [formValues.quizId, submissions],
  );

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography>Loading YouCat quiz settings...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Stack spacing={3}>
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "none",
            border: "1px solid rgba(15,23,42,0.08)",
          }}
        >
          <CardContent>
            <Typography variant="h6" fontWeight={700}>
              Quiz Availability and Schedule
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Participants can start the quiz only within this admin-defined
              window.
            </Typography>

            <Grid container spacing={3} mt={0.5}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Public Quiz Status"
                  value={formValues.isQuizAvailable ? "live" : "hidden"}
                  onChange={(event) =>
                    setFormValues((prev) => ({
                      ...prev,
                      isQuizAvailable: event.target.value === "live",
                    }))
                  }
                >
                  <MenuItem value="hidden">Hidden</MenuItem>
                  <MenuItem value="live">Live</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Quiz Start Date & Time"
                  value={formValues.startAt}
                  onChange={(event) =>
                    setFormValues((prev) => ({
                      ...prev,
                      startAt: event.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Quiz End Date & Time"
                  value={formValues.endAt}
                  onChange={(event) =>
                    setFormValues((prev) => ({
                      ...prev,
                      endAt: event.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Quiz Title"
                  value={formValues.title}
                  onChange={(event) =>
                    setFormValues((prev) => ({
                      ...prev,
                      title: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Total Attempt Time (mm:ss)"
                  value={durationInput}
                  onChange={(event) => setDurationInput(event.target.value)}
                  helperText={`Recommended: ${formatDurationLabel(
                    recommendedDurationSeconds,
                  )} | Minimum allowed: ${formatDurationLabel(
                    minimumDurationSeconds,
                  )}`}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Quiz Description"
                  value={formValues.description}
                  onChange={(event) =>
                    setFormValues((prev) => ({
                      ...prev,
                      description: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Participant Instructions"
                  value={formValues.instructions}
                  onChange={(event) =>
                    setFormValues((prev) => ({
                      ...prev,
                      instructions: event.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "none",
            border: "1px solid rgba(15,23,42,0.08)",
          }}
        >
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Quiz Builder
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  MCQ uses 30 seconds, short answer 45 seconds, and long answer
                  1 minute. Keyword checks support multiple entries and also
                  accept close or similar matches.
                </Typography>
              </Box>
              <Button startIcon={<Add />} variant="outlined" onClick={handleAddQuestion}>
                Add Question
              </Button>
            </Box>

            <Stack spacing={2} mt={3}>
              {formValues.questions.map((question, index) => (
                <Card
                  key={question.id || index}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid rgba(15,23,42,0.08)",
                  }}
                >
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={`Question ${index + 1}`}
                          value={question.question}
                          onChange={(event) =>
                            handleQuestionChange(index, "question", event.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          select
                          fullWidth
                          label="Type"
                          value={question.type}
                          onChange={(event) =>
                            handleQuestionChange(
                              index,
                              "type",
                              event.target.value as YoucatQuizQuestionType,
                            )
                          }
                        >
                          <MenuItem value="mcq">MCQ</MenuItem>
                          <MenuItem value="short_answer">Short Answer</MenuItem>
                          <MenuItem value="long_answer">Long Answer</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Points"
                          value={question.points}
                          onChange={(event) =>
                            handleQuestionChange(
                              index,
                              "points",
                              Number(event.target.value) || 0,
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <TextField
                          select
                          fullWidth
                          label="Required"
                          value={question.required ? "yes" : "no"}
                          onChange={(event) =>
                            handleQuestionChange(
                              index,
                              "required",
                              event.target.value === "yes",
                            )
                          }
                        >
                          <MenuItem value="no">No</MenuItem>
                          <MenuItem value="yes">Yes</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <IconButton
                          color="error"
                          onClick={() =>
                            setFormValues((prev) => ({
                              ...prev,
                              questions: prev.questions.filter(
                                (_, questionIndex) => questionIndex !== index,
                              ),
                            }))
                          }
                        >
                          <Delete />
                        </IconButton>
                      </Grid>

                      {question.type === "mcq" ? (
                        <>
                          <Grid item xs={12}>
                            <Stack spacing={1}>
                              {question.options.map((option, optionIndex) => (
                                <TextField
                                  key={`${question.id}-${optionIndex}`}
                                  fullWidth
                                  label={`Option ${optionIndex + 1}`}
                                  value={option}
                                  onChange={(event) =>
                                    handleQuestionChange(
                                      index,
                                      "options",
                                      question.options.map((item, itemIndex) =>
                                        itemIndex === optionIndex
                                          ? event.target.value
                                          : item,
                                      ),
                                    )
                                  }
                                />
                              ))}
                              <Button
                                variant="text"
                                onClick={() =>
                                  handleQuestionChange(index, "options", [
                                    ...question.options,
                                    `Option ${question.options.length + 1}`,
                                  ])
                                }
                              >
                                Add Option
                              </Button>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              select
                              fullWidth
                              label="Correct Option"
                              value={question.correctOption}
                              onChange={(event) =>
                                handleQuestionChange(
                                  index,
                                  "correctOption",
                                  event.target.value,
                                )
                              }
                            >
                              {question.options
                                .map((option) => option.trim())
                                .filter(Boolean)
                                .map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                            </TextField>
                          </Grid>
                        </>
                      ) : null}

                      {question.type === "short_answer" ? (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Accepted One-word Answers"
                            value={question.acceptedAnswers.join(", ")}
                            onChange={(event) =>
                              handleQuestionChange(
                                index,
                                "acceptedAnswers",
                                event.target.value
                                  .split(",")
                                  .map((item) => item.trim())
                                  .filter(Boolean),
                              )
                            }
                            helperText="Separate multiple accepted answers with commas"
                          />
                        </Grid>
                      ) : null}

                      {question.type !== "mcq" ? (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label={
                              question.type === "short_answer"
                                ? "Relevant Keywords"
                                : "Expected Keywords"
                            }
                            value={question.keywords.join(", ")}
                            onChange={(event) =>
                              handleQuestionChange(
                                index,
                                "keywords",
                                event.target.value
                                  .split(",")
                                  .map((item) => item.trim())
                                  .filter(Boolean),
                              )
                            }
                          />
                        </Grid>
                      ) : null}
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Alert severity="info">
          Warning prompts are built into the live quiz at 2 minutes, 1 minute 30
          seconds, 10 seconds, and then every second down to 0. Final marks stay
          pending until admin approval.
        </Alert>

        <Box>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <Button variant="contained" onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Quiz Settings"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteQuiz}
              disabled={isSaving}
            >
              Delete Quiz
            </Button>
          </Stack>
        </Box>

        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "none",
            border: "1px solid rgba(15,23,42,0.08)",
          }}
        >
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              mb={2}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Quiz Reviews and Excel
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Every quiz attempt stores participant details and answers for
                  export.
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() =>
                  downloadQuizWorkbook(
                    submissions.filter((item) => item.quizId === formValues.quizId),
                    formValues.title,
                  )
                }
                disabled={!submissions.some((item) => item.quizId === formValues.quizId)}
              >
                Download Quiz Excel
              </Button>
            </Box>

            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Pending Review
            </Typography>
            {!pendingSubmissions.length ? (
              <Typography color="text.secondary">No quiz attempts pending.</Typography>
            ) : (
              <Grid container spacing={2}>
                {pendingSubmissions.map((submission) => (
                  <Grid item xs={12} md={6} lg={4} key={submission.docId}>
                    <Card sx={{ height: "100%", borderRadius: 3 }}>
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" gap={1}>
                          <Chip
                            size="small"
                            color={
                              submission.approvalStatus === "rejected"
                                ? "error"
                                : "warning"
                            }
                            label={
                              submission.approvalStatus === "rejected"
                                ? "Rejected"
                                : "Pending"
                            }
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatQuizDateTime(
                              submission.submittedAt || submission.startedAt,
                            )}
                          </Typography>
                        </Stack>
                        <Typography variant="h6" fontWeight={700} mt={1.5}>
                          {submission.userProfile?.fullName || "Participant"}
                        </Typography>
                        <Typography color="text.secondary" mt={0.5}>
                          {submission.userProfile?.parish || ""} |{" "}
                          {submission.userProfile?.deanery || ""}
                        </Typography>
                        <Typography color="text.secondary" mt={1}>
                          Auto Score: {submission.autoScore}/{submission.maxScore}
                        </Typography>
                        <Typography color="text.secondary">
                          Answers saved:{" "}
                          {
                            Object.values(submission.answers || {}).filter((item) =>
                              String(item || "").trim(),
                            ).length
                          }
                          /{submission.questionSnapshots.length}
                        </Typography>
                        <Box mt={2}>
                          <Button
                            variant="outlined"
                            onClick={() => openReviewDialog(submission)}
                          >
                            Review Answers
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            <Typography variant="subtitle1" fontWeight={700} mt={4} mb={1}>
              Approved Attempts
            </Typography>
            {!approvedSubmissions.length ? (
              <Typography color="text.secondary">No approved quiz attempts yet.</Typography>
            ) : (
              <Grid container spacing={2}>
                {approvedSubmissions.map((submission) => (
                  <Grid item xs={12} md={6} lg={4} key={submission.docId}>
                    <Card sx={{ height: "100%", borderRadius: 3 }}>
                      <CardContent>
                        <Chip size="small" color="success" label="Approved" />
                        <Typography variant="h6" fontWeight={700} mt={1.5}>
                          {submission.userProfile?.fullName || "Participant"}
                        </Typography>
                        <Typography color="text.secondary" mt={0.5}>
                          {submission.userProfile?.parish || ""} |{" "}
                          {submission.userProfile?.deanery || ""}
                        </Typography>
                        <Typography color="text.secondary" mt={1}>
                          Final Score:{" "}
                          {typeof submission.finalScore === "number"
                            ? submission.finalScore
                            : submission.autoScore}
                          /{submission.maxScore}
                        </Typography>
                        <Box mt={2}>
                          <Button
                            variant="outlined"
                            onClick={() => openReviewDialog(submission)}
                          >
                            Open Review
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Dialog
        open={Boolean(selectedSubmission)}
        onClose={() => setSelectedSubmission(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Review Quiz Attempt</DialogTitle>
        <DialogContent dividers>
          {!selectedSubmission ? null : (
            <Stack spacing={2.5}>
              <Box>
                <Typography fontWeight={700}>
                  {selectedSubmission.userProfile?.fullName || "Participant"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedSubmission.userProfile?.parish || ""} |{" "}
                  {selectedSubmission.userProfile?.deanery || ""}
                </Typography>
              </Box>

              {selectedSubmission.questionSnapshots.map((question, index) => (
                <Card key={question.id} sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="overline" color="warning.main" fontWeight={800}>
                      Question {index + 1}
                    </Typography>
                    <Typography fontWeight={700} mt={0.5}>
                      {question.question}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Type: {question.type.replace("_", " ")} | Max marks:{" "}
                      {question.points}
                    </Typography>
                    <Typography variant="body2" mt={1.5}>
                      Participant answer:{" "}
                      {selectedSubmission.answers?.[question.id]?.trim() ||
                        "No answer"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Auto score:{" "}
                      {selectedSubmission.evaluationByQuestion?.[question.id]
                        ?.autoAwardedPoints || 0}
                      /{question.points}
                    </Typography>
                    {selectedSubmission.evaluationByQuestion?.[question.id]
                      ?.matchedKeywords?.length ? (
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Matched keywords:{" "}
                        {selectedSubmission.evaluationByQuestion[
                          question.id
                        ].matchedKeywords.join(", ")}
                      </Typography>
                    ) : null}
                    <TextField
                      fullWidth
                      type="number"
                      label="Final Marks Awarded"
                      value={reviewScoresByQuestion[question.id] || ""}
                      onChange={(event) =>
                        setReviewScoresByQuestion((prev) => ({
                          ...prev,
                          [question.id]: event.target.value,
                        }))
                      }
                      inputProps={{ min: 0, max: question.points, step: 0.5 }}
                      sx={{ mt: 2 }}
                    />
                  </CardContent>
                </Card>
              ))}

              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Review Remarks"
                value={reviewRemarks}
                onChange={(event) => setReviewRemarks(event.target.value)}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedSubmission(null)}>Close</Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleApproveSubmission("rejected")}
            disabled={reviewingSubmissionId === selectedSubmission?.docId}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            onClick={() => handleApproveSubmission("approved")}
            disabled={reviewingSubmissionId === selectedSubmission?.docId}
          >
            {reviewingSubmissionId === selectedSubmission?.docId
              ? "Saving..."
              : "Approve Final Marks"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
